import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Textarea from '@/components/ui/Textarea';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';

interface ReturnPolicy {
  accepts_returns: boolean;
  window_days: number;
  restocking_fee_percent: number;
  return_shipping: string;
}

interface SupplierForm {
  name: string;
  code: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  website: string;
  api_type: string;
  api_config: Record<string, string>;
  default_lead_time_days: string;
  payment_method: string;
  return_policy: ReturnPolicy;
  notes: string;
}

interface SupplierProduct {
  id: string;
  variant_id: string;
  variant_name: string;
  variant_sku: string;
  product_name: string;
  product_id: string;
  our_price: number;
  supplier_sku: string | null;
  cost_price: number | null;
  is_preferred: boolean;
  lead_time_days: number | null;
}

const API_TYPES = [
  { label: 'Manual (no integration)', value: 'none' },
  { label: 'Email notifications', value: 'email' },
  { label: 'API integration', value: 'api' },
  { label: 'Webhook', value: 'webhook' },
];

const PAYMENT_METHODS = [
  { label: 'Manual', value: 'manual' },
  { label: 'Platform Balance', value: 'platform_balance' },
  { label: 'Stripe Connect', value: 'stripe_connect' },
  { label: 'PayPal', value: 'paypal' },
  { label: 'Auto Invoice', value: 'invoice_auto' },
  { label: 'Wallet', value: 'wallet' },
];

const RETURN_SHIPPING_OPTIONS = [
  { label: 'Customer pays', value: 'customer' },
  { label: 'Supplier pays', value: 'supplier' },
  { label: 'Store pays', value: 'store' },
];

const INITIAL_FORM: SupplierForm = {
  name: '', code: '', contact_name: '', contact_email: '', contact_phone: '',
  website: '', api_type: 'none', api_config: {}, default_lead_time_days: '7',
  payment_method: 'manual',
  return_policy: { accepts_returns: true, window_days: 30, restocking_fee_percent: 0, return_shipping: 'customer' },
  notes: '',
};

const SupplierEditor = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = id && id !== 'new';
  const [formData, setFormData] = useState<SupplierForm>(INITIAL_FORM);
  const [linkedProducts, setLinkedProducts] = useState<SupplierProduct[]>([]);
  const [isLoading, setIsLoading] = useState(!!isEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const addToast = useUIStore((s) => s.addToast);

  useEffect(() => {
    if (!isEditMode) return;
    const fetchSupplier = async () => {
      try {
        setIsLoading(true);
        const { supplier } = await api.get<{ supplier: any }>(`/admin/suppliers/${id}`);
        setFormData({
          name: supplier.name,
          code: supplier.code,
          contact_name: supplier.contact_name || '',
          contact_email: supplier.contact_email || '',
          contact_phone: supplier.contact_phone || '',
          website: supplier.website || '',
          api_type: supplier.api_type || 'none',
          api_config: supplier.api_config || {},
          default_lead_time_days: String(supplier.default_lead_time_days || 7),
          payment_method: supplier.payment_method || 'manual',
          return_policy: supplier.return_policy || INITIAL_FORM.return_policy,
          notes: supplier.notes || '',
        });

        const { products } = await api.get<{ products: SupplierProduct[] }>(`/admin/suppliers/${id}/products`);
        setLinkedProducts(products);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load supplier');
      } finally {
        setIsLoading(false);
      }
    };
    fetchSupplier();
  }, [id, isEditMode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.code) {
      setError('Name and code are required');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const payload = {
        ...formData,
        default_lead_time_days: parseInt(formData.default_lead_time_days, 10) || 7,
      };

      if (isEditMode) {
        await api.put(`/admin/suppliers/${id}`, payload);
        addToast('Supplier updated', 'success');
      } else {
        const { supplier } = await api.post<{ supplier: any }>('/admin/suppliers', payload);
        addToast('Supplier created', 'success');
        navigate(`/admin/suppliers/${supplier.id}`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save supplier');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: keyof SupplierForm, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleUnlinkProduct = async (spId: string) => {
    try {
      await api.delete(`/admin/suppliers/${id}/products/${spId}`);
      setLinkedProducts((prev) => prev.filter((p) => p.id !== spId));
      addToast('Product unlinked', 'success');
    } catch {
      addToast('Failed to unlink product', 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/admin/suppliers" className="p-2 hover:bg-stone-100 rounded-lg">
          <ArrowLeft className="w-5 h-5 text-stone-600" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? formData.name || 'Edit Supplier' : 'New Supplier'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEditMode ? 'Update supplier details and integrations' : 'Add a new dropshipping supplier'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">{error}</div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main info */}
          <div className="lg:col-span-2 space-y-6">
            <Card title="Supplier Information" padding="md">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Supplier Name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  placeholder="e.g., Acme Supplies"
                />
                <Input
                  label="Code"
                  value={formData.code}
                  onChange={(e) => handleChange('code', e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                  required
                  placeholder="e.g., ACME"
                  helperText="Unique short code for this supplier"
                />
                <Input
                  label="Contact Name"
                  value={formData.contact_name}
                  onChange={(e) => handleChange('contact_name', e.target.value)}
                />
                <Input
                  label="Contact Email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => handleChange('contact_email', e.target.value)}
                />
                <Input
                  label="Contact Phone"
                  value={formData.contact_phone}
                  onChange={(e) => handleChange('contact_phone', e.target.value)}
                />
                <Input
                  label="Website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="https://..."
                />
              </div>
            </Card>

            <Card title="Integration" padding="md">
              <div className="space-y-4">
                <Select
                  label="Integration Type"
                  value={formData.api_type}
                  onChange={(e) => handleChange('api_type', e.target.value)}
                  options={API_TYPES}
                />
                {formData.api_type === 'email' && (
                  <p className="text-sm text-stone-500">
                    Orders will be forwarded via email to the supplier's contact email address.
                  </p>
                )}
                {formData.api_type === 'api' && (
                  <div className="space-y-3 p-3 bg-stone-50 rounded-lg">
                    <Input
                      label="API Endpoint URL"
                      value={formData.api_config.endpoint || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          api_config: { ...prev.api_config, endpoint: e.target.value },
                        }))
                      }
                      placeholder="https://api.supplier.com/orders"
                    />
                    <Input
                      label="API Key"
                      type="password"
                      value={formData.api_config.api_key || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          api_config: { ...prev.api_config, api_key: e.target.value },
                        }))
                      }
                      placeholder="sk-..."
                    />
                  </div>
                )}
                {formData.api_type === 'webhook' && (
                  <Input
                    label="Webhook URL"
                    value={formData.api_config.webhook_url || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        api_config: { ...prev.api_config, webhook_url: e.target.value },
                      }))
                    }
                    placeholder="https://hooks.supplier.com/orders"
                  />
                )}
              </div>
            </Card>

            {/* Linked Products (edit mode only) */}
            {isEditMode && (
              <Card title="Linked Products" padding="md">
                {linkedProducts.length === 0 ? (
                  <p className="text-sm text-stone-500 text-center py-4">
                    No products linked to this supplier yet.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-stone-50 border-b">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-stone-500 uppercase">Product / Variant</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-stone-500 uppercase">Our Price</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-stone-500 uppercase">Supplier Cost</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-stone-500 uppercase">Margin</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-stone-500 uppercase">Preferred</th>
                          <th className="px-4 py-2 w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {linkedProducts.map((lp) => {
                          const margin = lp.cost_price && lp.our_price
                            ? (((lp.our_price - lp.cost_price) / lp.our_price) * 100).toFixed(1)
                            : '-';
                          return (
                            <tr key={lp.id} className="hover:bg-stone-50">
                              <td className="px-4 py-2">
                                <div className="text-sm font-medium">{lp.product_name}</div>
                                <div className="text-xs text-stone-500">
                                  {lp.variant_name} ({lp.variant_sku})
                                  {lp.supplier_sku && ` / Supplier: ${lp.supplier_sku}`}
                                </div>
                              </td>
                              <td className="px-4 py-2 text-right text-sm">${parseFloat(String(lp.our_price)).toFixed(2)}</td>
                              <td className="px-4 py-2 text-right text-sm">
                                {lp.cost_price ? `$${parseFloat(String(lp.cost_price)).toFixed(2)}` : '-'}
                              </td>
                              <td className="px-4 py-2 text-right text-sm">
                                {margin !== '-' ? `${margin}%` : '-'}
                              </td>
                              <td className="px-4 py-2 text-center">
                                {lp.is_preferred && <span className="text-green-600 text-xs font-medium">Yes</span>}
                              </td>
                              <td className="px-4 py-2">
                                <button
                                  type="button"
                                  onClick={() => handleUnlinkProduct(lp.id)}
                                  className="p-1 text-red-400 hover:text-red-600"
                                  title="Unlink product"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card title="Settings" padding="md">
              <div className="space-y-4">
                <Input
                  label="Default Lead Time (days)"
                  type="number"
                  min="1"
                  value={formData.default_lead_time_days}
                  onChange={(e) => handleChange('default_lead_time_days', e.target.value)}
                />
                <Select
                  label="Payment Method"
                  value={formData.payment_method}
                  onChange={(e) => handleChange('payment_method', e.target.value)}
                  options={PAYMENT_METHODS}
                />
                <Textarea
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={4}
                  placeholder="Internal notes about this supplier..."
                />
              </div>
            </Card>

            <Card title="Return Policy" padding="md">
              <div className="space-y-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.return_policy.accepts_returns}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        return_policy: { ...prev.return_policy, accepts_returns: e.target.checked },
                      }))
                    }
                    className="rounded border-stone-300"
                  />
                  <span className="text-sm text-stone-700">Accepts returns</span>
                </label>
                {formData.return_policy.accepts_returns && (
                  <>
                    <Input
                      label="Return Window (days)"
                      type="number"
                      min="0"
                      value={String(formData.return_policy.window_days)}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          return_policy: { ...prev.return_policy, window_days: parseInt(e.target.value, 10) || 0 },
                        }))
                      }
                    />
                    <Input
                      label="Restocking Fee (%)"
                      type="number"
                      min="0"
                      max="100"
                      value={String(formData.return_policy.restocking_fee_percent)}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          return_policy: { ...prev.return_policy, restocking_fee_percent: parseInt(e.target.value, 10) || 0 },
                        }))
                      }
                    />
                    <Select
                      label="Return Shipping"
                      value={formData.return_policy.return_shipping}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          return_policy: { ...prev.return_policy, return_shipping: e.target.value },
                        }))
                      }
                      options={RETURN_SHIPPING_OPTIONS}
                    />
                  </>
                )}
              </div>
            </Card>

            <Button variant="primary" size="md" type="submit" isLoading={isSaving} className="w-full">
              <Save className="w-5 h-5 mr-2" />
              {isEditMode ? 'Save Changes' : 'Create Supplier'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SupplierEditor;
